<template>
  <div class="kakaomap-shell">
    <KakaoMap
      :lat="mapCenter.lat"
      :lng="mapCenter.lng"
      :level="mapLevel"
      @onLoadKakaoMap="onLoadKakaoMap"
    >
      <KakaoMapMarker
        v-if="markerPosition"
        :lat="markerPosition.lat"
        :lng="markerPosition.lng"
        :infoWindow="infoWindow"
        :clickable="true"
        @onClickKakaoMapMarker="onClickMarker"
        class="test"
      />
    </KakaoMap>
    <div class="location-info" v-if="addressLoaded">
      <i class="ph-fill ph-map-pin"></i>
      <span>{{ locationAddress }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, inject, defineProps, defineEmits } from 'vue';
import { KakaoMap, KakaoMapMarker } from 'vue3-kakao-maps';

const sql_query = inject('sql_query');

const props = defineProps({
  location: {
    type: String,
    default: ''
  },
  customerId: {
    type: [Number, String],
    default: null
  }
});

const emit = defineEmits(['coordsFound']);

const map = ref(null);
const geocoder = ref(null);
const mapCenter = ref({ lat: 36.187082, lng: 127.084889 });
const mapLevel = ref(5);
const markerPosition = ref(null);
const infoWindow = ref({ content: '', visible: true });
const addressLoaded = ref(false);
const locationAddress = ref('');

const onLoadKakaoMap = (mapRef) => {
  map.value = mapRef;
  
  if (window.kakao && window.kakao.maps && window.kakao.maps.services) {
    geocoder.value = new window.kakao.maps.services.Geocoder();
    
    if (props.location) {
      searchLocation(props.location);
    }
  }
};

const searchLocation = (query) => {
  if (!geocoder.value || !query) return;

  // 먼저 주소 검색 시도
  geocoder.value.addressSearch(query, (result, status) => {
    if (status === window.kakao.maps.services.Status.OK) {
      displayLocation(result[0].y, result[0].x, query);
    } else {
      // 주소 검색 실패시 키워드 검색
      searchKeyword(query);
    }
  });
};

const searchKeyword = (keyword) => {
  const ps = new window.kakao.maps.services.Places();
  
  ps.keywordSearch(keyword, (data, status) => {
    if (status === window.kakao.maps.services.Status.OK) {
      const firstResult = data[0];
      displayLocation(firstResult.y, firstResult.x, firstResult.place_name || keyword);
    } else {
      console.error('검색 실패:', status);
    }
  });
};

const displayLocation = async (lat, lng, name) => {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  
  // 마커 위치 설정
  markerPosition.value = {
    lat: latNum,
    lng: lngNum
  };
  
  // 인포윈도우 설정
  infoWindow.value = {
    content: `<div style="padding:8px 12px; box-sizing:border-box; font-size:14px; font-weight:600;">${name}</div>`,
    visible: true
  };
  
  // 지도 중심 이동
  mapCenter.value = {
    lat: latNum,
    lng: lngNum
  };
  mapLevel.value = 3;
  
  // 지도 객체가 있으면 직접 이동
  if (map.value) {
    const coords = new window.kakao.maps.LatLng(lat, lng);
    map.value.setCenter(coords);
    map.value.setLevel(3);
  }
  
  locationAddress.value = name;
  addressLoaded.value = true;
  
  // 좌표 저장 (customerId가 있으면)
  if (props.customerId) {
    await saveCoords(latNum, lngNum);
  }
  
  emit('coordsFound', { lat: latNum, lng: lngNum });
};

// DB에 좌표 저장
const saveCoords = async (lat, lng) => {
  try {
    await sql_query(
      `UPDATE customers SET lat = ${lat}, lng = ${lng} WHERE idx = ${props.customerId}`
    );
    console.log('좌표 저장 완료:', lat, lng);
  } catch (error) {
    console.error('좌표 저장 실패:', error);
  }
};

const onClickMarker = () => {
  infoWindow.value.visible = !infoWindow.value.visible;
};

// location prop 변경 감지
watch(() => props.location, (newVal) => {
  if (newVal && geocoder.value) {
    searchLocation(newVal);
  }
});
</script>

<style scoped>
.location-info {
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--color-background);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
}

.location-info i {
  color: var(--color-brand);
  font-size: var(--font-size-lg);
}

.kakaomap-shell {
  position: relative;
  display:flex;
  flex-direction: column;
  overflow: hidden;
  color:#000;
}

.kakaomap-shell > div {
  position: relative !important;
  width:100% !important;
}

.kakaomap-shell div {
  box-sizing: border-box !important;
}
</style>